import { useCallback, useEffect, useId, useMemo, useRef, useState } from "react";
import { useStore } from "zustand";
import { Button } from "@/components/ui";
import type { Draft, DraftRequest, PageEvent, PublicPage, PublicPlatform } from "@/lib/api/types";
import { NOT_STORED_LINE } from "@/lib/copy";
import { overflowBy } from "@/lib/platform";
import { type ProofPool, ReviewApiError, createProofPool, requestDrafts } from "../api";
import { copyForPlatform, openForPlatform } from "../copyFlow";
import { demoDrafts } from "../demo";
import { buildDraftRequest } from "../logic";
import { type Gen, createReviewStore, limitsOf } from "../store";
import { type CopyResult, CopyConfirmDialog } from "./CopyConfirmDialog";
import { PlatformButtons } from "./PlatformButtons";
import { ReviewHeader } from "./ReviewHeader";
import { StepChips } from "./StepChips";
import { StepDrafts } from "./StepDrafts";
import { StepProgress } from "./StepProgress";
import { StepQuestions } from "./StepQuestions";

export type FlowMode = "live" | "preview" | "demo";

export interface ReviewFlowProps {
  page: PublicPage;
  /** live: real API. preview: console, no generation. demo: landing and /r/demo, local drafts. */
  mode: FlowMode;
  /** Rendered inside another page (landing, console): no h1, focus does not jump the page. */
  embedded?: boolean;
  onEvent?: (event: PageEvent) => void;
}

const STEP_TITLES = {
  1: "어떤 점이 좋았어요?",
  2: "조금만 더 알려 주세요",
  3: "마음에 드는 초안을 골라 주세요",
} as const;

const BUSY_WAIT_DEFAULT = 20;

/**
 * Server error codes (api/services/drafts.py): 429 busy (retry_after) counts down and retries,
 * 429 quota and 503 daily_cap switch to "write it yourself", 409 paused, 422 invalid_choice,
 * 503 unavailable and network errors offer a retry.
 */
export function errorToGen(err: unknown): Gen {
  const e = err instanceof ReviewApiError ? err : null;
  if (e?.code === "quota" || e?.code === "daily_cap") {
    return { kind: "manual", reason: "quota", message: e.message };
  }
  if (e?.status === 429) {
    return {
      kind: "error",
      code: "busy",
      message: e.message || "지금 찾는 분이 많아요. 잠깐만 기다려 주세요.",
      waitSeconds: e.retryAfter ?? BUSY_WAIT_DEFAULT,
    };
  }
  if (e?.code === "paused") {
    return {
      kind: "error",
      code: "paused",
      message: e.message || "지금은 후기를 받지 않아요. 찾아 주셔서 고마워요.",
      waitSeconds: null,
    };
  }
  if (e?.code === "invalid_choice") {
    return {
      kind: "error",
      code: "invalid_choice",
      message: `${e.message} 이전으로 돌아가 다시 골라 주세요.`,
      waitSeconds: null,
    };
  }
  return {
    kind: "error",
    code: e?.code ?? "unavailable",
    message: e?.message ?? "지금은 초안을 만들지 못했어요. 잠시 뒤 다시 시도해 주세요.",
    waitSeconds: null,
  };
}

/**
 * The customer helper: three steps, one question per screen, then copy and open. The same
 * component runs the real page, the console preview and the landing demo.
 *
 * Accessibility: focus moves to the step heading on every step change, buttons are never
 * disabled (pressing explains why through the aria-live line), touch targets are 48px+,
 * selection is color plus a check icon.
 */
export function ReviewFlow({ page, mode, embedded = false, onEvent }: ReviewFlowProps) {
  const store = useMemo(() => createReviewStore(page), [page]);
  const s = useStore(store);
  const uid = useId();
  const headingRef = useRef<HTMLHeadingElement>(null);
  const textRef = useRef<HTMLTextAreaElement>(null);
  const movedRef = useRef(false);
  const roundRef = useRef(0);
  const poolRef = useRef<ProofPool | null>(null);
  const [copyResult, setCopyResult] = useState<CopyResult | null>(null);

  const primary = page.platforms[0];
  const HeadingTag = embedded ? "h3" : "h2";

  // Live mode: start solving a challenge right away so it is ready by step 3.
  useEffect(() => {
    if (mode !== "live") return;
    const pool = createProofPool();
    poolRef.current = pool;
    pool.warm();
  }, [mode]);

  // Step change: focus the new question so screen readers start there. Not on first paint.
  useEffect(() => {
    if (!movedRef.current) return;
    const h = headingRef.current;
    if (!h) return;
    h.focus({ preventScroll: true });
    h.scrollIntoView({ block: "nearest" });
  }, [s.step]);

  const generate = useCallback(async () => {
    const st = store.getState();
    const req: DraftRequest = buildDraftRequest(page, st);
    st.setNotice("");
    if (mode === "preview") {
      st.setGen({ kind: "manual", reason: "preview" });
      return;
    }
    st.setGen({ kind: "loading" });
    if (mode === "demo") {
      roundRef.current += 1;
      const round = roundRef.current;
      await new Promise((r) => setTimeout(r, 700));
      store.getState().setGen({ kind: "ready", drafts: demoDrafts(req, round - 1) });
      return;
    }
    const pool = poolRef.current ?? createProofPool();
    poolRef.current = pool;
    try {
      let drafts: Draft[];
      try {
        drafts = await requestDrafts(page.slug, await pool.take(), req);
      } catch (err) {
        // Expired or already used challenge: solve a fresh one and try once more.
        if (!(err instanceof ReviewApiError && err.code === "challenge_invalid")) throw err;
        drafts = await requestDrafts(page.slug, await pool.fresh(), req);
      }
      store.getState().setGen({ kind: "ready", drafts });
    } catch (err) {
      store.getState().setGen(errorToGen(err));
    } finally {
      pool.warm();
    }
  }, [mode, page, store]);

  // 429 busy: count down, then retry on its own.
  const waitSeconds = s.gen.kind === "error" ? s.gen.waitSeconds : null;
  useEffect(() => {
    if (waitSeconds === null) return;
    if (waitSeconds <= 0) {
      void generate();
      return;
    }
    const t = window.setTimeout(() => store.getState().tick(), 1000);
    return () => window.clearTimeout(t);
  }, [waitSeconds, generate, store]);

  const onNext = () => {
    const from = store.getState().step;
    if (store.getState().next()) {
      movedRef.current = true;
      if (from === 2) void generate();
    }
  };

  const onBack = () => {
    movedRef.current = true;
    store.getState().back();
  };

  const onPickPlatform = async (p: PublicPlatform) => {
    const st = store.getState();
    if (st.gen.kind === "loading" || st.gen.kind === "idle") {
      st.setNotice("초안이 다 만들어지면 눌러 주세요.");
      return;
    }
    if (st.gen.kind === "error") {
      st.setNotice("초안을 다시 만들거나 직접 써 주신 뒤 눌러 주세요.");
      return;
    }
    if (mode === "preview" && !st.text.trim()) {
      setCopyResult({
        platform: p,
        ok: true,
        copied: "(고객이 고른 초안이 여기에 들어가요)",
        title: "",
        restoredTail: false,
      });
      return;
    }
    if (!st.text.trim()) {
      st.setNotice("올릴 글이 비어 있어요. 초안을 고르거나 한두 문장 적어 주세요.");
      textRef.current?.focus();
      return;
    }
    const over = overflowBy(st.text.trim(), p.body_max);
    if (over > 0) {
      st.setNotice(`${p.name}에는 ${p.body_max}자까지 올라가요. ${over}자만 줄여 주세요.`);
      textRef.current?.focus();
      return;
    }
    const draft = st.gen.kind === "ready" ? st.gen.drafts[st.choice] : undefined;
    const outcome = await copyForPlatform({
      text: st.text,
      title: p.has_title ? st.title : "",
      draft,
      platform: p,
      sponsored: page.sponsored,
      onEvent: mode === "live" ? onEvent : undefined,
    });
    if (outcome.restoredTail) st.setText(outcome.copied);
    st.setNotice(
      outcome.ok ? "복사했어요. 정말 고마워요." : "복사가 되지 않았어요. 글을 직접 복사해 주세요."
    );
    setCopyResult({
      platform: p,
      ok: outcome.ok,
      copied: outcome.copied,
      title: p.has_title ? st.title.trim() : "",
      restoredTail: outcome.restoredTail,
    });
  };

  const onOpenPlatform = (p: PublicPlatform) => {
    openForPlatform({ platform: p, onEvent: mode === "live" ? onEvent : undefined });
    setCopyResult(null);
  };

  return (
    <div className="break-keep text-headline-2 font-medium text-label-normal">
      <ReviewHeader page={page} embedded={embedded} />
      <StepProgress step={s.step} />

      <HeadingTag
        ref={headingRef}
        tabIndex={-1}
        className="mt-4 wds-title-3 text-label-strong focus:outline-none"
      >
        {STEP_TITLES[s.step]}
      </HeadingTag>

      {s.step === 1 ? (
        <StepChips
          goodChips={page.good_chips}
          badChips={page.bad_chips}
          good={s.good}
          bad={s.bad}
          goodMax={limitsOf(page).good_max}
          badMax={limitsOf(page).bad_max}
          onToggleGood={s.toggleGood}
          onToggleBad={s.toggleBad}
        />
      ) : s.step === 2 ? (
        <StepQuestions
          questions={page.questions}
          answers={s.answers}
          tone={s.tone}
          blockedQuestion={s.blockedQuestion}
          name={uid}
          onAnswer={s.answer}
          onTone={s.setTone}
        />
      ) : (
        <StepDrafts
          gen={s.gen}
          choice={s.choice}
          text={s.text}
          title={s.title}
          primary={primary}
          sponsored={page.sponsored}
          name={uid}
          textRef={textRef}
          demo={mode === "demo"}
          onPick={s.pickDraft}
          onText={s.setText}
          onTitle={s.setTitle}
          onRetry={() => void generate()}
          onWriteSelf={() => {
            store.getState().setGen({ kind: "manual", reason: "self" });
            setTimeout(() => textRef.current?.focus(), 0);
          }}
        />
      )}

      <output
        aria-live="polite"
        className="mt-4 block min-h-6 text-body-1 font-semibold text-primary-normal"
      >
        {s.notice}
      </output>

      <div className="mt-4 flex flex-col gap-3">
        {s.step === 1 ? (
          <Button
            size="large"
            fullWidth
            onClick={onNext}
            style={{ fontSize: "var(--font-size-headline-2)" }}
          >
            다음
          </Button>
        ) : s.step === 2 ? (
          <div className="flex gap-2">
            <Button
              size="large"
              variant="outlined"
              color="assistive"
              onClick={onBack}
              style={{ fontSize: "var(--font-size-headline-2)" }}
            >
              이전
            </Button>
            <Button
              size="large"
              fullWidth
              onClick={onNext}
              style={{ flex: 1, fontSize: "var(--font-size-headline-2)" }}
            >
              초안 만들기
            </Button>
          </div>
        ) : (
          <>
            <PlatformButtons platforms={page.platforms} onPick={(p) => void onPickPlatform(p)} />
            <Button
              size="large"
              wrap
              variant="text"
              color="assistive"
              onClick={onBack}
              style={{ fontSize: "var(--font-size-body-1)" }}
            >
              이전으로 돌아가 다시 고르기
            </Button>
          </>
        )}
      </div>

      <footer className="mt-10 flex flex-col gap-2 text-label-1 text-label-neutral">
        <p>초안은 고르신 항목으로만 만들어요. {NOT_STORED_LINE}</p>
        {page.show_badge ? (
          <p>
            <a href="/" className="underline underline-offset-4 hover:text-label-normal">
              텔뷰로 만든 후기 도우미
            </a>
          </p>
        ) : null}
      </footer>

      <CopyConfirmDialog
        result={copyResult}
        mode={mode}
        onClose={() => setCopyResult(null)}
        onOpen={onOpenPlatform}
      />
    </div>
  );
}
